import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import Link from "~/components/Link";
import { Logo, LogoText } from "~/components/Logo";
import { HelpButtonFab } from "~/components/HelpButtonFab";
import RumProvider from "~/components/RumProvider";
import { TeamSearchBar } from "~/app/(new)/[teamId]/TeamSearchBar";
import { TeamPicker } from "~/app/(new)/[teamId]/TeamPicker";
import { UserDropdown } from "~/app/(new)/[teamId]/UserDropdown";
import { PlaygroundRedirector } from "~/app/(new)/[teamId]/PlaygroundRedirector";
import { SignInSignUpLocalStorageSetter } from "~/app/(new)/[teamId]/SignInSignUpLocalStorageSetter";
import { trpcStandalone } from "~/utils/trpcStandalone";

/**
 * Team layout (chrome + team context). Fetches the signed-in user, the active
 * team, the team list, and the user's role, then renders the top nav + Outlet.
 * (Was [teamId]/layout.tsx.)
 */
export const Route = createFileRoute("/_authed/$teamId")({
  loader: async ({ params }) => {
    const { teamId } = params;
    const me = await trpcStandalone.auth.me.query();
    const [team, teams, role] = await Promise.all([
      trpcStandalone.team.getTeamById.query({ id: teamId }),
      trpcStandalone.team.getTeams.query(),
      trpcStandalone.team.getTeamRole.query({ teamId, personId: me.id }),
    ]);

    // Users without a password (and not Google SSO) must set one first.
    if (!me.hasPassword && !me.hasSignedInWithGoogle) {
      throw redirect({ href: "/verify-email-success-set-password" });
    }

    return { me, team, teams, role };
  },
  component: TeamLayout,
});

function TeamLayout() {
  const { teamId } = Route.useParams();
  const { me, team, teams, role } = Route.useLoaderData();

  return (
    <RumProvider>
      <div className="grid h-screen w-full grid-rows-[max-content_minmax(0,1fr)]">
        <nav className="border-base-content/20 grid grid-cols-[max-content_1fr_max-content] md:grid-cols-[30ch_1fr_25ch_5ch] place-items-center gap-2 border-b p-2 bg-base-300 items-center">
          <Link
            href={`/${teamId}${team.isPlaygroundOnly ? "/playground" : ""}`}
            className="flex flex-row items-center gap-2"
          >
            <Logo className="h-6 w-6" />
            <LogoText className="text-lg hidden md:block" />
          </Link>

          <div className="hidden md:block">
            {team.isPlaygroundOnly ? <div /> : <TeamSearchBar />}
          </div>

          <TeamPicker teams={teams} team={team} />

          <UserDropdown me={me} team={team} role={role} teamId={teamId} />
        </nav>
        <div className="w-full h-full overflow-auto">
          <Outlet />
        </div>
        <HelpButtonFab />
        <PlaygroundRedirector />
        <SignInSignUpLocalStorageSetter />
      </div>
    </RumProvider>
  );
}
